import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Check, ChevronRight, Settings2, X } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPerformance,
  CommercialOutcomeWindowDays,
  MarketingPerformance,
  PerformanceFigures,
  PerformanceSummary,
  PerformanceTargets,
  SendPerformance,
} from "@shared-core/data/marketing-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  type TargetStatus,
  campaignCostTotal,
  costReturns,
  formatCostPerLead,
  formatRate,
  formatRoi,
  performanceRates,
  performanceSendLabel,
  sendCostTotal,
  targetStatuses,
  upliftCampaignCosts,
} from "@shared-core/domain/campaign-performance";
import { formatUplift } from "@shared-core/domain/campaign-uplift";
import {
  formatCount,
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { ToggleGroup, ToggleGroupItem } from "../../primitives/toggle-group";
import { CampaignCostsDialog } from "./CampaignCostsDialog";
import { PerformanceTargetsDialog } from "./PerformanceTargetsDialog";

const WINDOW_OPTIONS: CommercialOutcomeWindowDays[] = [
  5, 7, 10, 14, 30, 45, 90,
];
// The window the Performance tab opens on; must match the route's default,
// which the API keeps warm (marketingResultsWindowDefaults.test.ts).
export const DEFAULT_PERFORMANCE_WINDOW: CommercialOutcomeWindowDays = 45;

type ColumnGroup = "engagement" | "sales" | "costs";

const COLUMN_GROUP_LABELS: Record<ColumnGroup, string> = {
  engagement: "Engagement",
  sales: "Sales",
  costs: "Costs & ROI",
};

const COLUMNS: Record<ColumnGroup, string[]> = {
  engagement: ["Contacts", "Delivered", "Opened", "Clicked", "Unsubscribed"],
  sales: ["Sales", "Sales £", "Uplift", "New services"],
  costs: [
    "Costs",
    "Leads",
    "Response rate",
    "Cost per lead",
    "ROI on uplift",
    "ROI on sales",
  ],
};

interface CampaignPerformanceSectionProps {
  canManage: boolean;
}

/**
 * The Results page's Performance tab: every email send's actuals against the
 * targets, grouped by campaign, with costs and ROI. Replaces the marketing
 * manager's "Perf - Email" spreadsheet. See
 * docs/features/campaign-performance-targets/01_product_brief.md.
 */
export function CampaignPerformanceSection({
  canManage,
}: CampaignPerformanceSectionProps) {
  const dataAdapter = useDataAdapter();
  const [windowDays, setWindowDays] = useState<CommercialOutcomeWindowDays>(
    DEFAULT_PERFORMANCE_WINDOW,
  );
  const [performance, setPerformance] = useState<MarketingPerformance | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [group, setGroup] = useState<ColumnGroup>("engagement");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [costsCampaignId, setCostsCampaignId] = useState<string | null>(null);
  const [costsOpen, setCostsOpen] = useState(false);
  const [targetsOpen, setTargetsOpen] = useState(false);

  const load = useCallback(
    async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      setError(false);
      try {
        setPerformance(await dataAdapter.getMarketingPerformance(windowDays));
      } catch (e) {
        console.error("marketing.performance.load.failed", e);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [dataAdapter, windowDays],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const toggle = (campaignId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });

  const costsCampaign =
    performance?.campaigns.find((c) => c.campaign_id === costsCampaignId) ??
    null;
  const totalCosts =
    performance?.campaigns.reduce(
      (sum, c) => sum + campaignCostTotal(c.costs),
      0,
    ) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg">Performance</h2>
          <p className="text-sm text-muted-foreground">
            Every email send against its targets, with costs and return.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(windowDays)}
            onValueChange={(v) =>
              setWindowDays(Number(v) as CommercialOutcomeWindowDays)
            }
          >
            <SelectTrigger className="w-52" aria-label="Sales window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  Sales window: {days} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canManage && performance && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTargetsOpen(true)}
            >
              <Settings2 className="size-4" />
              Targets
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">
          Loading performance…
        </div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600 dark:text-red-400">
          Couldn't load performance. Please try again.
        </div>
      )}

      {!loading &&
        !error &&
        performance &&
        performance.campaigns.length === 0 && (
          <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
            No email sends yet. Performance appears here once a campaign's first
            send goes out.
          </div>
        )}

      {!loading &&
        !error &&
        performance &&
        performance.campaigns.length > 0 && (
          <>
            <SummaryToDate
              summary={performance.summary}
              targets={performance.targets}
              totalCosts={totalCosts}
              upliftCosts={upliftCampaignCosts(performance.campaigns)}
            />

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted-foreground">Show:</span>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={group}
                onValueChange={(v) => v && setGroup(v as ColumnGroup)}
                aria-label="Columns to show"
              >
                {(Object.keys(COLUMN_GROUP_LABELS) as ColumnGroup[]).map(
                  (g) => (
                    <ToggleGroupItem key={g} value={g} className="px-3">
                      {COLUMN_GROUP_LABELS[g]}
                    </ToggleGroupItem>
                  ),
                )}
              </ToggleGroup>
            </div>

            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign / send</TableHead>
                    {COLUMNS[group].map((label) => (
                      <TableHead key={label} className="text-right">
                        {label}
                      </TableHead>
                    ))}
                    {group === "costs" && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {performance.campaigns.map((campaign) => (
                    <Fragment key={campaign.campaign_id}>
                      <CampaignRow
                        campaign={campaign}
                        group={group}
                        targets={performance.targets}
                        isExpanded={expanded.has(campaign.campaign_id)}
                        onToggle={() => toggle(campaign.campaign_id)}
                        onOpenCosts={() => {
                          setCostsCampaignId(campaign.campaign_id);
                          setCostsOpen(true);
                        }}
                        canManage={canManage}
                      />
                      {expanded.has(campaign.campaign_id) &&
                        campaign.sends.map((send) => (
                          <SendRow
                            key={send.step_id}
                            campaignId={campaign.campaign_id}
                            send={send}
                            campaign={campaign}
                            group={group}
                            targets={performance.targets}
                            canManage={canManage}
                            onPlanRefSaved={() => void load(false)}
                          />
                        ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            <PerformanceNotes windowDays={windowDays} />
          </>
        )}

      <CampaignCostsDialog
        open={costsOpen}
        campaign={costsCampaign}
        canManage={canManage}
        onOpenChange={setCostsOpen}
        onChanged={() => void load(false)}
      />
      {performance && (
        <PerformanceTargetsDialog
          open={targetsOpen}
          targets={performance.targets}
          onOpenChange={setTargetsOpen}
          onSaved={() => void load(false)}
        />
      )}
    </div>
  );
}

function StatusMark({ status }: { status: TargetStatus | null }) {
  if (status === null) return null;
  return status === "met" ? (
    <Check
      className="inline size-4 text-emerald-600 dark:text-emerald-400"
      aria-label="On target"
    />
  ) : (
    <X
      className="inline size-4 text-red-600 dark:text-red-400"
      aria-label="Missed target"
    />
  );
}

function SummaryTile({
  label,
  value,
  detail,
  status,
  emphasis = false,
}: {
  label: string;
  value: string;
  detail?: ReactNode;
  status?: TargetStatus | null;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        emphasis ? "border-primary/40 bg-primary/5" : "border-border"
      }`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-1.5 text-lg font-medium tabular-nums">
        {value}
        {status !== undefined && <StatusMark status={status} />}
      </div>
      {detail && (
        <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
      )}
    </div>
  );
}

/** Totals across every campaign shown. Uplift, and so ROI on uplift, count
 *  only the campaigns that are a fair comparison, against those campaigns'
 *  own costs; the tile says how many it left out. */
function SummaryToDate({
  summary,
  targets,
  totalCosts,
  upliftCosts,
}: {
  summary: PerformanceSummary;
  targets: PerformanceTargets;
  totalCosts: number;
  upliftCosts: number;
}) {
  const figures = summary;
  const rates = performanceRates(figures);
  const statuses = targetStatuses(rates, targets);
  const returns = costReturns(figures, totalCosts > 0 ? totalCosts : null);
  const roiOnUplift = costReturns(
    figures,
    upliftCosts > 0 ? upliftCosts : null,
  ).roiOnUplift;
  const excluded = summary.uplift_excluded_campaign_count;
  const excludedNote =
    excluded > 0
      ? ` · excludes ${excluded} campaign${excluded === 1 ? "" : "s"} without a fair comparison`
      : "";
  return (
    <section aria-label="Summary to date" className="space-y-2">
      <h3 className="text-sm font-medium">Summary to date</h3>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <SummaryTile
          label="Contacts"
          value={formatCount(figures.contacts)}
          detail={`Delivered ${formatRate(rates.delivered)}`}
        />
        <SummaryTile
          label="Opened"
          value={formatRate(rates.opened)}
          status={statuses.opened}
          detail={`Target ${formatRate(targets.open_rate)}`}
        />
        <SummaryTile
          label="Clicked"
          value={formatRate(rates.clicked)}
          status={statuses.clicked}
          detail={`Target ${formatRate(targets.click_rate)}`}
        />
        <SummaryTile
          label="Unsubscribed"
          value={formatRate(rates.unsubscribed)}
          status={statuses.unsubscribed}
          detail={`Maximum ${formatRate(targets.max_unsubscribe_rate)}`}
        />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <SummaryTile
          label="Sales £"
          value={formatCurrencyGbp(figures.sales_amount)}
          detail={`${formatCount(figures.sales_count)} sales · ${formatCount(
            figures.new_services_count,
          )} new services`}
        />
        <SummaryTile
          label="Uplift"
          value={formatUplift(
            figures.uplift_is_comparable ? figures.uplift_amount : null,
            null,
          )}
          detail={`Extra sales above normal ordering${excludedNote}`}
        />
        <SummaryTile
          label="Costs"
          value={totalCosts > 0 ? formatCurrencyGbp(totalCosts) : "—"}
          detail={`Cost per lead ${formatCostPerLead(returns.costPerLead)}`}
        />
        <SummaryTile
          label="ROI on uplift"
          value={formatRoi(roiOnUplift)}
          detail={`Return on the extra sales${excludedNote}`}
          emphasis
        />
        <SummaryTile
          label="ROI on sales"
          value={formatRoi(returns.roiOnSales)}
          detail="Includes orders that would have come anyway"
        />
      </div>
    </section>
  );
}

function RateCell({
  count,
  rate,
  status,
}: {
  count: number;
  rate: number | null;
  status?: TargetStatus | null;
}) {
  return (
    <span className="inline-flex items-center justify-end gap-1 tabular-nums">
      {formatCount(count)}
      <span className="text-muted-foreground">({formatRate(rate)})</span>
      {status !== undefined && <StatusMark status={status} />}
    </span>
  );
}

/** A table cell's content; a bare node fills one column. */
type FigureCell = ReactNode | { content: ReactNode; colSpan: number };

function isSpanningCell(
  cell: FigureCell,
): cell is { content: ReactNode; colSpan: number } {
  return typeof cell === "object" && cell !== null && "colSpan" in cell;
}

function FigureCells({ cells }: { cells: FigureCell[] }) {
  return (
    <>
      {cells.map((cell, i) =>
        isSpanningCell(cell) ? (
          <TableCell key={i} colSpan={cell.colSpan} className="text-right">
            {cell.content}
          </TableCell>
        ) : (
          <TableCell key={i} className="text-right whitespace-nowrap">
            {cell}
          </TableCell>
        ),
      )}
    </>
  );
}

function figureCells(
  group: ColumnGroup,
  figures: PerformanceFigures,
  targets: PerformanceTargets,
  cost: number | null,
  engagementAvailable: boolean,
): FigureCell[] {
  const rates = performanceRates(figures);
  if (group === "engagement") {
    if (!engagementAvailable) {
      return [
        formatCount(figures.contacts),
        {
          content: (
            <span className="text-muted-foreground">
              Awaiting Mailchimp: opens and clicks arrive the morning after the
              send
            </span>
          ),
          colSpan: 4,
        },
      ];
    }
    const statuses = targetStatuses(rates, targets);
    return [
      formatCount(figures.contacts),
      <RateCell count={figures.delivered} rate={rates.delivered} />,
      <RateCell
        count={figures.opened}
        rate={rates.opened}
        status={statuses.opened}
      />,
      <RateCell
        count={figures.clicked}
        rate={rates.clicked}
        status={statuses.clicked}
      />,
      <RateCell
        count={figures.unsubscribed}
        rate={rates.unsubscribed}
        status={statuses.unsubscribed}
      />,
    ];
  }
  if (group === "sales") {
    return [
      formatCount(figures.sales_count),
      formatCurrencyGbp(figures.sales_amount),
      figures.uplift_is_comparable ? (
        formatUplift(figures.uplift_amount, null)
      ) : (
        <span className="text-muted-foreground">Not a fair comparison</span>
      ),
      <RateCell count={figures.new_services_count} rate={rates.new_services} />,
    ];
  }
  const returns = costReturns(figures, cost);
  return [
    cost === null ? "—" : formatCurrencyGbp(cost, 2),
    engagementAvailable ? formatCount(figures.clicked) : "—",
    engagementAvailable ? formatRate(rates.response) : "—",
    formatCostPerLead(returns.costPerLead),
    <span className="font-medium">{formatRoi(returns.roiOnUplift)}</span>,
    formatRoi(returns.roiOnSales),
  ];
}

function CampaignRow({
  campaign,
  group,
  targets,
  isExpanded,
  onToggle,
  onOpenCosts,
  canManage,
}: {
  campaign: CampaignPerformance;
  group: ColumnGroup;
  targets: PerformanceTargets;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenCosts: () => void;
  canManage: boolean;
}) {
  const cost =
    campaign.costs.length > 0 ? campaignCostTotal(campaign.costs) : null;
  const engagementAvailable = campaign.sends.some(
    (s) => s.engagement_available,
  );
  const cells = figureCells(
    group,
    campaign.figures,
    targets,
    cost,
    engagementAvailable,
  );
  return (
    <TableRow className="bg-muted/30">
      <TableCell>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className="flex items-center gap-1.5 text-left font-medium hover:text-primary"
        >
          <ChevronRight
            className={`size-4 shrink-0 transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
          {campaign.campaign_name}
        </button>
        <div className="ml-6 text-xs text-muted-foreground">
          {campaign.campaign_kind === "always_on"
            ? "Always on · sales so far"
            : campaign.sends.length === 1
              ? "1 send"
              : `${campaign.sends.length} sends`}
        </div>
      </TableCell>
      <FigureCells cells={cells} />
      {group === "costs" && (
        <TableCell className="text-right">
          <Button variant="outline" size="sm" onClick={onOpenCosts}>
            {campaign.costs.length > 0
              ? canManage
                ? "Costs"
                : "View costs"
              : canManage
                ? "Add cost"
                : "No costs"}
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

function SendRow({
  campaignId,
  campaign,
  send,
  group,
  targets,
  canManage,
  onPlanRefSaved,
}: {
  campaignId: string;
  campaign: CampaignPerformance;
  send: SendPerformance;
  group: ColumnGroup;
  targets: PerformanceTargets;
  canManage: boolean;
  onPlanRefSaved: () => void;
}) {
  const cells = figureCells(
    group,
    send.figures,
    targets,
    sendCostTotal(campaign.costs, send.step_id),
    send.engagement_available,
  );
  return (
    <TableRow>
      <TableCell className="pl-8">
        <div className="flex items-center gap-2">
          <PlanRef
            campaignId={campaignId}
            send={send}
            label={performanceSendLabel(send, campaign.sends)}
            canManage={canManage}
            onSaved={onPlanRefSaved}
          />
          <span>{performanceSendLabel(send, campaign.sends)}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          Sent {formatDateOrDash(send.sent_at)}
          {!send.window_complete && " · sales so far"}
        </div>
      </TableCell>
      <FigureCells cells={cells} />
      {group === "costs" && <TableCell />}
    </TableRow>
  );
}

function PlanRef({
  campaignId,
  send,
  label,
  canManage,
  onSaved,
}: {
  campaignId: string;
  send: SendPerformance;
  label: string;
  canManage: boolean;
  onSaved: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(send.plan_ref ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorId = `plan-ref-error-${send.step_id}`;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.setSendPlanRef(
        campaignId,
        send.step_id,
        value.trim() || null,
      );
      setEditing(false);
      onSaved();
    } catch (e) {
      console.error("marketing.performance.plan_ref_save.failed", e);
      const fieldError = extractFieldErrors(e).find(
        (f) => f.field === "plan_ref",
      );
      setError(fieldError?.message ?? "Couldn't save");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="EM-000"
          aria-label={`Plan ref for ${label}`}
          aria-invalid={error !== null}
          aria-describedby={error ? errorId : undefined}
          className="h-7 w-24 text-xs"
        />
        <Button type="submit" size="sm" className="h-7 px-2" disabled={saving}>
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={() => {
            setValue(send.plan_ref ?? "");
            setEditing(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
        {error && (
          <span id={errorId} className="text-xs text-red-600 dark:text-red-400">
            {error}
          </span>
        )}
      </form>
    );
  }

  if (send.plan_ref) {
    return canManage ? (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="rounded border border-border px-1.5 py-0.5 font-mono text-xs hover:border-primary"
        aria-label={`Edit plan ref ${send.plan_ref}`}
      >
        {send.plan_ref}
      </button>
    ) : (
      <span className="rounded border border-border px-1.5 py-0.5 font-mono text-xs">
        {send.plan_ref}
      </span>
    );
  }

  return canManage ? (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="rounded border border-dashed border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-foreground"
    >
      + Plan ref
    </button>
  ) : null;
}

function PerformanceNotes({ windowDays }: { windowDays: number }) {
  return (
    <div className="space-y-2 text-xs text-muted-foreground">
      <p>
        <span className="font-medium text-foreground">
          How sales are counted.
        </span>{" "}
        A sale counts for a send when the customer was sent it and ordered from
        that day up to {windowDays} days after the campaign's end date. If they
        were sent more than one email, it goes to the most recent one before
        they ordered. It counts whether or not they opened the email, so it's
        timing, not proof the email caused the order. "Sales so far" means the
        campaign's end date plus {windowDays} days hasn't passed yet, or the
        campaign is always on. A campaign's own row also counts sales credited
        to its mailings and call rounds, which have no rows here.
      </p>
      <p>
        <span className="font-medium text-foreground">Uplift</span> is Sales £
        minus normal ordering: what the same customers would usually have
        ordered over those days, judged from their last 12 months and how
        customers who weren't emailed did. A send's normal ordering covers the
        days its sales are counted for, so a campaign's sends add up to the
        campaign. When almost everyone emailed already orders regularly, there's
        nobody fair to compare with, so it says "Not a fair comparison", and the
        Summary to date leaves that campaign out of Uplift and ROI on uplift.
      </p>
      <p>
        <span className="font-medium text-foreground">ROI on uplift</span> is
        (Uplift − Costs) ÷ Costs: the return on the extra sales. It's the fairer
        measure.{" "}
        <span className="font-medium text-foreground">ROI on sales</span> uses
        all sales, including orders that would have come anyway, so it flatters
        campaigns to existing customers. Costs entered against a whole campaign
        count only on the campaign's row; a send shows costs and ROI only when a
        cost is tied to it.
      </p>
      <p>
        <span className="font-medium text-foreground">Opened and Clicked</span>{" "}
        are a share of Delivered, and count each person once.{" "}
        <span className="font-medium text-foreground">Unsubscribed</span> is a
        share of Contacts. Leads are the people who clicked; Response rate is
        Leads ÷ Contacts.
      </p>
    </div>
  );
}
